import React, { useState, useEffect } from "react";
import { useCollection } from "../../hooks/useCollection";
import { AdminOnly } from "../../components/RoleBasedAccess";
import AnaliticaFilter from "./AnaliticaFilter";
import "./AnaliticaAdmin.css";

export default function AnaliticaAdmin() {
  const [analiticaData, setAnaliticaData] = useState([]);
  const [projectsData, setProjectsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(0); // 0 = tutti i mesi
  const [totaliPerSettore, setTotaliPerSettore] = useState({});
  const [totaliPerCategoria, setTotaliPerCategoria] = useState({});
  const [selectedCategoria, setSelectedCategoria] = useState(''); // Filtro per categoria
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSettore, setSelectedSettore] = useState(null);
  const [settoreRecords, setSettoreRecords] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableCategorie, setAvailableCategorie] = useState([]);

  // Hook per recuperare i dati dalla collection analitica
  const { documents: analitica, error: analiticaError } = useCollection("analitica");
  const { documents: projects, error: projectsError } = useCollection("projects");

  // Rimuovo il controllo manuale admin - ora gestito da AdminOnly component

  // Elabora i dati dei progetti per creare una mappa codice -> progetto
  useEffect(() => {
    if (projects) {
      const projectMap = {};
      projects.forEach(project => {
        if (project.codice) {
          projectMap[project.codice] = {
            descrizione: project.dettagli || project.nome || "Nessuna descrizione disponibile",
            nome: project.nome || "Nome non disponibile",
            categoria: project.categoria || "Non categorizzato",
            codice: project.codice,
            cliente: project.cliente || "Cliente non specificato",
            tipoProject: project.tipoProject || "Tipo non specificato"
          };
        }
      });
      setProjectsData(projectMap);
    }
  }, [projects]);

  // Elabora i dati analitici
  useEffect(() => {
    if (analitica && !analiticaError) {
      // Filtra i dati in base all'anno, al mese e alla categoria selezionati
      let filteredData = analitica.filter(item => {
        const yearMatch = !selectedYear || item.anno === selectedYear;
        const monthMatch = selectedMonth === 0 || item.mese === selectedMonth;
        
        // Filtro per categoria: trova la categoria del settore tramite projectsData
        let categoriaMatch = true;
        if (selectedCategoria) {
          const project = projectsData[item.settore];
          if (project) {
            categoriaMatch = project.categoria === selectedCategoria;
          } else {
            categoriaMatch = selectedCategoria === 'Non categorizzato';
          }
        }
        
        // Filtro per termine di ricerca
        let searchMatch = true;
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          const settoreMatch = item.settore.toString().toLowerCase().includes(searchLower);
          const project = projectsData[item.settore];
          const projectNameMatch = project && project.nome && project.nome.toLowerCase().includes(searchLower);
          const clienteMatch = project && project.cliente && project.cliente.toLowerCase().includes(searchLower);
          searchMatch = settoreMatch || projectNameMatch || clienteMatch;
        }
        
        return yearMatch && monthMatch && categoriaMatch && searchMatch;
      });

      setAnaliticaData(filteredData);

      // Calcola i totali per settore
      const totali = {};
      const totaliCategoria = {};
      
      filteredData.forEach(item => {
        const settore = item.settore;
        const project = projectsData[settore];
        const categoria = project?.categoria || 'Non categorizzato';
        
        // Totali per settore
        if (!totali[settore]) {
          totali[settore] = {
            ricavi: 0,
            materiali: 0,
            dipendenti: 0,
            lavorazioni: 0,
            struttura: 0,
            personale: 0,
            rappresentanza: 0,
            automezzi: 0,
            count: 0,
            categoria: categoria
          };
        }

        totali[settore].ricavi += item.ricavi || 0;
        totali[settore].materiali += item.materiali || 0;
        totali[settore].dipendenti += item.dipendenti || 0;
        totali[settore].lavorazioni += item.lavorazioni || 0;
        totali[settore].struttura += item.struttura || 0;
        totali[settore].personale += item.personale || 0;
        totali[settore].rappresentanza += item.rappresentanza || 0;
        totali[settore].automezzi += item.automezzi || 0;
        totali[settore].count += 1;
        
        // Totali per categoria
        if (!totaliCategoria[categoria]) {
          totaliCategoria[categoria] = {
            ricavi: 0,
            materiali: 0,
            dipendenti: 0,
            lavorazioni: 0,
            struttura: 0,
            personale: 0,
            rappresentanza: 0,
            automezzi: 0,
            count: 0,
            settori: new Set()
          };
        }
        
        totaliCategoria[categoria].ricavi += item.ricavi || 0;
        totaliCategoria[categoria].materiali += item.materiali || 0;
        totaliCategoria[categoria].dipendenti += item.dipendenti || 0;
        totaliCategoria[categoria].lavorazioni += item.lavorazioni || 0;
        totaliCategoria[categoria].struttura += item.struttura || 0;
        totaliCategoria[categoria].personale += item.personale || 0;
        totaliCategoria[categoria].rappresentanza += item.rappresentanza || 0;
        totaliCategoria[categoria].automezzi += item.automezzi || 0;
        totaliCategoria[categoria].count += 1;
        totaliCategoria[categoria].settori.add(settore);
      });

      setTotaliPerSettore(totali);
      setTotaliPerCategoria(totaliCategoria);
      setLoading(false);
    }
  }, [analitica, analiticaError, selectedYear, selectedMonth, selectedCategoria, searchTerm, projectsData]);

  // Gestisce gli errori
  useEffect(() => {
    if (analiticaError || projectsError) {
      setError(analiticaError || projectsError);
      setLoading(false);
    }
  }, [analiticaError, projectsError]);

  // Funzioni di gestione dei filtri
  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
  };

  const handleCategoriaChange = (categoria) => {
    setSelectedCategoria(categoria);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };



  // Aggiorna gli anni e le categorie disponibili quando i dati cambiano
  useEffect(() => {
    if (analiticaData) {
      const years = [...new Set(analiticaData.map(item => item.anno))];
      setAvailableYears(years.sort((a, b) => b - a));
    }
    
    if (projectsData) {
      const categorie = [...new Set(Object.values(projectsData).map(project => project.categoria).filter(Boolean))];
      setAvailableCategorie(categorie.sort());
    }
  }, [analiticaData, projectsData]);

  // Formatta i numeri in valuta
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  // Calcola il margine lordo per categoria per un mese specifico
  const calcolaMargineLordoCategoriaMese = (categoria, anno, mese) => {
    if (!analitica) return 0;
    
    const datiMese = analitica.filter(item => {
      const project = projectsData[item.settore];
      const categoriaItem = project?.categoria || 'Non categorizzato';
      return item.anno === anno && item.mese === mese && categoriaItem === categoria;
    });
    
    let ricaviTotali = 0;
    let costiTotali = 0;
    
    datiMese.forEach(item => {
      ricaviTotali += item.ricavi || 0;
      costiTotali += (item.materiali || 0) + (item.dipendenti || 0) + (item.lavorazioni || 0) + 
                     (item.struttura || 0) + (item.personale || 0) + (item.rappresentanza || 0) + (item.automezzi || 0);
    });
    
    return ricaviTotali - costiTotali;
  };

  // Calcola la differenza del margine lordo cumulativo rispetto al mese precedente
  const calcolaDifferenzaMarginePrecedente = (categoria) => {
    // Solo quando si visualizzano dati annuali (selectedMonth === 0)
    if (selectedMonth !== 0) return null;
    
    if (!analitica) return null;
    
    // Trova tutti i mesi disponibili per questa categoria nell'anno selezionato
    const mesiDisponibili = analitica
      .filter(item => {
        const project = projectsData[item.settore];
        const categoriaItem = project?.categoria || 'Non categorizzato';
        return item.anno === selectedYear && categoriaItem === categoria;
      })
      .map(item => item.mese)
      .filter((mese, index, array) => array.indexOf(mese) === index)
      .sort((a, b) => a - b);
    
    if (mesiDisponibili.length < 2) return null; // Serve almeno 2 mesi per il confronto
    
    // L'ultimo e penultimo mese disponibili
    const ultimoMese = Math.max(...mesiDisponibili);
    const penultimoMese = mesiDisponibili[mesiDisponibili.length - 2];
    
    // Calcola il margine cumulativo fino all'ultimo mese (incluso)
    let margineCumulativoUltimoMese = 0;
    for (let mese = 1; mese <= ultimoMese; mese++) {
      if (mesiDisponibili.includes(mese)) {
        margineCumulativoUltimoMese += calcolaMargineLordoCategoriaMese(categoria, selectedYear, mese);
      }
    }
    
    // Calcola il margine cumulativo fino al penultimo mese (incluso)
    let margineCumulativoPenultimoMese = 0;
    for (let mese = 1; mese <= penultimoMese; mese++) {
      if (mesiDisponibili.includes(mese)) {
        margineCumulativoPenultimoMese += calcolaMargineLordoCategoriaMese(categoria, selectedYear, mese);
      }
    }
    
    // La differenza indica l'incremento/decremento del margine
    return margineCumulativoUltimoMese - margineCumulativoPenultimoMese;
  };

  // Formatta la differenza con indicatore di trend
  const formatDifferenzaMargineTrend = (differenza) => {
    if (differenza === null) return null;
    
    const isPositivo = differenza >= 0;
    const simbolo = isPositivo ? '↗' : '↘';
    const classe = isPositivo ? 'trend-positivo' : 'trend-negativo';
    
    return {
      valore: formatCurrency(Math.abs(differenza)),
      simbolo: simbolo,
      classe: classe,
      isPositivo: isPositivo
    };
  };

  // Calcola il margine per settore
  const calcolaMargineLordo = (settore) => {
    const totale = totaliPerSettore[settore];
    if (!totale) return 0;
    
    const costiTotali = totale.materiali + totale.dipendenti + totale.lavorazioni + 
                       totale.struttura + totale.personale + totale.rappresentanza + totale.automezzi;
    return totale.ricavi - costiTotali;
  };

  // Calcola la percentuale di margine
  const calcolaPercentualeMargineLordo = (settore) => {
    const totale = totaliPerSettore[settore];
    if (!totale || totale.ricavi === 0) return 0;
    
    const margineLordo = calcolaMargineLordo(settore);
    return (margineLordo / totale.ricavi) * 100;
  };

  // Calcola il totale dei costi per settore
  const calcolaTotaleCosti = (settore) => {
    const totale = totaliPerSettore[settore];
    if (!totale) return 0;
    
    return totale.materiali + totale.dipendenti + totale.lavorazioni + 
           totale.struttura + totale.personale + totale.rappresentanza + totale.automezzi;
  };

  // Gestione modal
  const handleCardClick = (settore) => {
    const records = analiticaData.filter(item => item.settore === settore);
    setSelectedSettore(settore);
    setSettoreRecords(records);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedSettore(null);
    setSettoreRecords([]);
  };

  // Gestione scroll del body quando il modal è aperto (stessa soluzione di FoglioPresenza)
  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [modalOpen]);

  // Formatta la data
  const formatDate = (anno, mese) => {
    const mesi = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ];
    return `${mesi[mese - 1]} ${anno}`;
  };

  // Genera l'indicatore visuale dei mesi
  const renderMonthIndicator = (settore) => {
    const settoreData = analiticaData.filter(item => item.settore === settore);
    const mesiPresenti = new Set(settoreData.map(item => item.mese));
    
    const mesiNomi = ['G', 'F', 'M', 'A', 'M', 'G', 'L', 'A', 'S', 'O', 'N', 'D'];
    
    return (
      <div className="month-indicator">
        {mesiNomi.map((mese, index) => {
          const meseNumero = index + 1;
          const isActive = mesiPresenti.has(meseNumero);
          return (
            <div 
              key={index} 
              className={`month-circle ${isActive ? 'active' : 'inactive'}`}
              title={`${mese === 'G' && index === 0 ? 'Gennaio' : 
                      mese === 'F' ? 'Febbraio' : 
                      mese === 'M' && index === 2 ? 'Marzo' : 
                      mese === 'A' && index === 3 ? 'Aprile' : 
                      mese === 'M' && index === 4 ? 'Maggio' : 
                      mese === 'G' && index === 5 ? 'Giugno' : 
                      mese === 'L' ? 'Luglio' : 
                      mese === 'A' && index === 7 ? 'Agosto' : 
                      mese === 'S' ? 'Settembre' : 
                      mese === 'O' ? 'Ottobre' : 
                      mese === 'N' ? 'Novembre' : 'Dicembre'} ${isActive ? '(presente)' : '(assente)'}`}
            >
              {mese}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <AdminOnly>
        <div className="analitica-admin-container">
          <div className="loading">Caricamento dati analitici...</div>
        </div>
      </AdminOnly>
    );
  }

  if (error) {
    return (
      <AdminOnly>
        <div className="analitica-admin-container">
          <div className="error">{error}</div>
        </div>
      </AdminOnly>
    );
  }

  return (
    <AdminOnly>
      <div className="analitica-admin-container">
      <h1>Analisi Dati Settoriali</h1>
      
      {/* Filtri */}
      <AnaliticaFilter
        selectedYear={selectedYear}
        onYearChange={handleYearChange}
        selectedMonth={selectedMonth}
        onMonthChange={handleMonthChange}
        selectedCategoria={selectedCategoria}
        onCategoriaChange={handleCategoriaChange}
        searchTerm={searchTerm}
        onSearch={handleSearch}
        availableYears={availableYears}
        availableCategorie={availableCategorie}
      />

      {/* Riepilogo generale */}
      <div className="riepilogo-generale">
        <h2>Riepilogo Generale</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Settori Analizzati</h3>
            <p className="stat-number">{Object.keys(totaliPerSettore).length}</p>
          </div>
          <div className="stat-card">
            <h3>Categorie Analizzate</h3>
            <p className="stat-number">{Object.keys(totaliPerCategoria).length}</p>
          </div>
          <div className="stat-card">
            <h3>Record Totali</h3>
            <p className="stat-number">{analiticaData.length}</p>
          </div>
          <div className="stat-card">
            <h3>Ricavi Totali</h3>
            <p className="stat-number">
              {formatCurrency(Object.values(totaliPerSettore).reduce((sum, settore) => sum + settore.ricavi, 0))}
            </p>
          </div>
          <div className="stat-card">
            <h3>Margine Lordo Totale</h3>
            <p className="stat-number">
              {formatCurrency(Object.keys(totaliPerSettore).reduce((sum, settore) => sum + calcolaMargineLordo(settore), 0))}
            </p>
          </div>
        </div>
      </div>

      {/* Analisi per Categoria */}
      {Object.keys(totaliPerCategoria).length > 0 && (
        <div className="analisi-categorie">
          <h2>Analisi per Categoria</h2>
          <div className="categorie-grid">
            {Object.entries(totaliPerCategoria).map(([categoria, totale]) => {
              const totaleCostiCategoria = totale.materiali + totale.dipendenti + totale.lavorazioni + totale.struttura + totale.personale + totale.rappresentanza + totale.automezzi;
              const margineLordoCategoria = totale.ricavi - totaleCostiCategoria;
              const percentualeMargineLordoCategoria = totale.ricavi > 0 ? (margineLordoCategoria / totale.ricavi) * 100 : 0;
              
              // Calcola la differenza rispetto al mese precedente
              const differenzaMarginePrecedente = calcolaDifferenzaMarginePrecedente(categoria);
              const trendData = formatDifferenzaMargineTrend(differenzaMarginePrecedente);
              
              return (
                <div key={categoria} className="categoria-card">
                  <div className="categoria-header">
                    <h3>{categoria}</h3>
                    <div className="categoria-info">
                      <span className="settori-count">{totale.settori.size} settori</span>
                      <span className="record-count">{totale.count} record</span>
                    </div>
                  </div>
                  
                  <div className="categoria-stats">
                    <div className="stat-item">
                      <span className="stat-label">Ricavi:</span>
                      <span className="stat-value ricavi">{formatCurrency(totale.ricavi)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Totale Costi:</span>
                      <span className="stat-value totale-costi">{formatCurrency(totaleCostiCategoria)}</span>
                    </div>
                    
                    {/* Dettaglio costi per categoria */}
                    <div className="costi-dettaglio-categoria">
                      <h5 className="costi-dettaglio-title">Dettaglio Costi:</h5>
                      <div className="costi-grid">
                        <div className="costo-item">
                          <span className="costo-label">Materiali:</span>
                          <span className="costo-value">{formatCurrency(totale.materiali)}</span>
                        </div>
                        <div className="costo-item">
                          <span className="costo-label">Dipendenti:</span>
                          <span className="costo-value">{formatCurrency(totale.dipendenti)}</span>
                        </div>
                        <div className="costo-item">
                          <span className="costo-label">Lavorazioni:</span>
                          <span className="costo-value">{formatCurrency(totale.lavorazioni)}</span>
                        </div>
                        <div className="costo-item">
                          <span className="costo-label">Struttura:</span>
                          <span className="costo-value">{formatCurrency(totale.struttura)}</span>
                        </div>
                        <div className="costo-item">
                          <span className="costo-label">Personale:</span>
                          <span className="costo-value">{formatCurrency(totale.personale)}</span>
                        </div>
                        <div className="costo-item">
                          <span className="costo-label">Rappresentanza:</span>
                          <span className="costo-value">{formatCurrency(totale.rappresentanza)}</span>
                        </div>
                        <div className="costo-item">
                          <span className="costo-label">Automezzi:</span>
                          <span className="costo-value">{formatCurrency(totale.automezzi)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="stat-item">
                      <span className="stat-label">Margine Lordo:</span>
                      <span className={`stat-value ${margineLordoCategoria >= 0 ? 'margine-positivo' : 'margine-negativo'}`}>
                        {formatCurrency(margineLordoCategoria)} ({percentualeMargineLordoCategoria.toFixed(1)}%)
                      </span>
                    </div>
                    
                    {/* Trend incremento margine - solo per visualizzazione annuale */}
                    {selectedMonth === 0 && trendData && (
                      <div className="stat-item trend-item">
                        <span className="stat-label">Incremento Margine:</span>
                        <span className={`stat-value trend-value ${trendData.classe}`}>
                          <span className="trend-simbolo">{trendData.simbolo}</span>
                          <span className="trend-valore">{trendData.valore}</span>
                          <span className="trend-descrizione">
                            {trendData.isPositivo ? 'in crescita' : 'in calo'}
                          </span>
                        </span>
                      </div>
                    )}
                    <div className="settori-lista">
                      <span className="stat-label">Settori inclusi:</span>
                      <div className="settori-tags">
                        {Array.from(totale.settori).map(settore => (
                          <span key={settore} className="settore-tag">{settore}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabella dettagliata per settore */}
      <div className="tabella-settori">
        <h2>Analisi per Settore</h2>
        {Object.keys(totaliPerSettore).length === 0 ? (
          <p>Nessun dato disponibile per i filtri selezionati.</p>
        ) : (
          <div className="settori-grid">
            {Object.entries(totaliPerSettore).map(([settore, totale]) => (
              <div key={settore} className="settore-card" onClick={() => handleCardClick(settore)}>
                <div className="settore-header">
                  <div className="settore-title">
                    <h3 className="settore-nome">{settore}</h3>
                    <span className="settore-categoria">{totale.categoria}</span>
                  </div>
                  {renderMonthIndicator(settore)}
                </div>
                
                {/* Informazioni dal progetto */}
                {projectsData[settore] ? (
                  <div className="settore-descrizione">
                    <h4>Progetto: {projectsData[settore].nome}</h4>
                    <div className="progetto-dettagli">
                      <p><strong>Codice:</strong> {projectsData[settore].codice}</p>
                      <p><strong>Cliente:</strong> {projectsData[settore].cliente}</p>
                      <p><strong>Tipo:</strong> {projectsData[settore].tipoProject}</p>
                      <p><strong>Descrizione:</strong> {projectsData[settore].descrizione}</p>
                    </div>
                  </div>
                ) : (
                  <div className="settore-descrizione">
                    <h4>Settore: {settore}</h4>
                    <p className="warning">⚠️ Nessun progetto associato trovato per il codice {settore}</p>
                  </div>
                )}
                
                {/* Dati finanziari */}
                <div className="dati-finanziari">
                  <div className="ricavi-section">
                    <h4>Ricavi: {formatCurrency(totale.ricavi)}</h4>
                  </div>
                  
                  <div className="costi-section">
                    <h5>Costi:</h5>
                    <ul>
                      <li>Materiali: {formatCurrency(totale.materiali)}</li>
                      <li>Dipendenti: {formatCurrency(totale.dipendenti)}</li>
                      <li>Lavorazioni: {formatCurrency(totale.lavorazioni)}</li>
                      <li>Struttura: {formatCurrency(totale.struttura)}</li>
                      <li>Personale: {formatCurrency(totale.personale)}</li>
                      <li>Rappresentanza: {formatCurrency(totale.rappresentanza)}</li>
                      <li>Automezzi: {formatCurrency(totale.automezzi)}</li>
                    </ul>
                  </div>
                  
                  <div className="totale-costi-section">
                    <h4 className="totale-costi">
                      Totale Costi: {formatCurrency(calcolaTotaleCosti(settore))}
                    </h4>
                  </div>
                  
                  <div className="margine-section">
                    <h4 className={calcolaMargineLordo(settore) >= 0 ? 'margine-positivo' : 'margine-negativo'}>
                      Margine Lordo: {formatCurrency(calcolaMargineLordo(settore))}
                    </h4>
                    <p className="percentuale-margine">
                      ({calcolaPercentualeMargineLordo(settore).toFixed(2)}%)
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal per dettagli settore */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dettagli Settore: {selectedSettore}</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            
            {projectsData[selectedSettore] && (
              <div className="modal-project-info">
                <h3>Informazioni Progetto</h3>
                <div className="project-details-modal">
                  <p><strong>Nome:</strong> {projectsData[selectedSettore].nome}</p>
                  <p><strong>Codice:</strong> {projectsData[selectedSettore].codice}</p>
                  <p><strong>Cliente:</strong> {projectsData[selectedSettore].cliente}</p>
                  <p><strong>Categoria:</strong> {projectsData[selectedSettore].categoria}</p>
                  <p><strong>Tipo:</strong> {projectsData[selectedSettore].tipoProject}</p>
                </div>
              </div>
            )}
            
            <div className="modal-body">
              <h3>Record Analitici ({settoreRecords.length})</h3>
              {settoreRecords.length === 0 ? (
                <p>Nessun record trovato per questo settore.</p>
              ) : (
                <div className="records-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Periodo</th>
                        <th>Ricavi</th>
                        <th>Materiali</th>
                        <th>Dipendenti</th>
                        <th>Lavorazioni</th>
                        <th>Struttura</th>
                        <th>Personale</th>
                        <th>Rappresentanza</th>
                        <th>Automezzi</th>
                        <th>Margine</th>
                      </tr>
                    </thead>
                    <tbody>
                       {settoreRecords
                         .sort((a, b) => {
                           // Ordina prima per anno, poi per mese
                           if (a.anno !== b.anno) {
                             return a.anno - b.anno;
                           }
                           return a.mese - b.mese;
                         })
                         .map((record, index) => {
                         const margine = (record.ricavi || 0) - 
                           ((record.materiali || 0) + (record.dipendenti || 0) + 
                            (record.lavorazioni || 0) + (record.struttura || 0) + 
                            (record.personale || 0) + (record.rappresentanza || 0) + 
                            (record.automezzi || 0));
                         return (
                           <tr key={index}>
                             <td>{formatDate(record.anno, record.mese)}</td>
                             <td>{formatCurrency(record.ricavi)}</td>
                             <td>{formatCurrency(record.materiali)}</td>
                             <td>{formatCurrency(record.dipendenti)}</td>
                             <td>{formatCurrency(record.lavorazioni)}</td>
                             <td>{formatCurrency(record.struttura)}</td>
                             <td>{formatCurrency(record.personale)}</td>
                             <td>{formatCurrency(record.rappresentanza)}</td>
                             <td>{formatCurrency(record.automezzi)}</td>
                             <td className={margine >= 0 ? 'margine-positivo' : 'margine-negativo'}>
                               {formatCurrency(margine)}
                             </td>
                           </tr>
                         );
                       })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminOnly>
  );
}
